/* eslint-disable @stylistic/max-len */
import { ApiStoreConfig, ApiStoreXmedia } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiCmsTranslations } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-translations/api-cms-translations.models';


export const DEFAULT_LANGUAGE_ID = '-1'; // EN (English)

export interface StoreSummary {
  catalogs: StoreCatalog[]; /** Catalog list */
  continent: string; /** Continent identifier */
  countryCode: string; /** Country code (uppercase), e.g. ES */
  countryName: string; /** Country name.
                        * In theory, it should come localized in the requested language, but in practice this is usually not the case
                        */
  hostname: string; /** Host identifier to use in that store, e.g. in China the domain changes */
  id: string; /** Identifier */
  supportedLanguages: StoreLanguage[]; /** Supported languages list */
  isOpenForSale: boolean; /** Indicates whether the store can sell or not */
  warehouseId: number; /** Identifier of the warehouse used */
  redirectToWorldWide?: number; /** Store ID to redirect to in non-selling countries (franchisees), e.g. 68009600 */
  countryISOredirect?: string; /** Country ISO used to generate redirect to WW/WZ/WY, etc., e.g. WW */
}

// TODO Translations Review
// Review Update translations type with model from back-api
// Should translations be at this level? better inside summary? better elsewhere?
export interface StoreDetail extends StoreSummary {
  details: StoreDetailDetails; /** Store details */
  storeDefaultLanguageId?: string; /** Default language identifier */
}

export interface StoreLanguage {
  code: string; /** Language code */
  id: string; /** Language identifier */
  name: string; /** Language name */
  direction: 'rtl' | 'ltr'; /** Indicates if is a right-to-left language (ex. arabic/hebrew) or left-to-right (ex. spanish) */
  languageTag?: string; /** Language tag in BCP 47 format (e.g. "en_US") */
}

export interface StoreCatalog {
  id: string; /** Catalog identifier */
  type?: string; /** Catalog type */
  label?: string; /** Catalog descriptive label */
  identifier?: string;
}

export interface StoreDetailDetails {
  currencyInfo?: CurrencyInfo; /** Currency information based on country */
  alternateLocaleInfo?: AlternateLocaleInfo;
  location?: StoreDetailLocation;
  timestampBuildVersion?: string;
  imageBaseUrl?: string; /** Base path for images (non-xmedia) */
  staticBaseUrl?: string; /** Base path for static files */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _xmedia?: ApiStoreXmedia; /** Raw API information useful for building media URLs with xmedia */
  translations?: ApiCmsTranslations; /** Static component translations */
  internationalSizes?: Record<string, string>; /** International sizes */
  configurations?: ApiStoreConfig[];
  storeAvailability?: boolean; /** Indicates whether the store has stock or not */
}

export interface StoreDetailLocation {
  latitude: number; /** Central latitude of the market */
  longitude: number; /** Central longitude of the market */
  zoom: number; /** Zoom level to apply on the rendered map */
}

export interface CurrencyInfo {
  currencyCode: string; /** Currency code @example "EUR" */
  currencyFormatNeg: string; /** Mask for negative prices @example "-#,##0.00 € " */
  currencyFormatPos: string; /** Mask for positive prices @example "#,##0.00 € " */
  currencyDecimals: number; /** Number of decimals (base-10 exponent) @example -2 */
  currencySymbol: string; /** Currency symbol @example "€" */
  currencyDecimalChar: string; /** Decimal separator character @example "," */
  currencyGroupingChar: string; /** Grouping separator character @example "." */
}

export interface AlternateLocaleInfo extends CurrencyInfo {
  conversionRate: number; /** Conversion rate used to calculate local currency value in the alternate currency */
}

/** Stores Helpers Models */
export interface ContinentStores {
  id: string; /** Continent identifier */
  name: string; /** Continent name */
  nameES: string; /** Continent name in Spanish */
  stores: StoreSummary[]; /** Store list */
}

export enum SalesStatusTypes {
  WORLD,
  SALE,
  NO_SALE,
  WORLD_WIDE
}

export interface GroupStore {
  id: SalesStatusTypes; /** Market identifier */
  name: string; /** Market name */
  continents: ContinentStores[]; /** Continent list */
}


// TODO: improve name and remove from store.models?
export interface StoresMap {
  [name: string]: StoreSummary; /** Stores grouped by countryCode */
}

export type StoreXmedia = ApiStoreXmedia;

export interface ContinentStoresConfig {
  id: string;
  name: string;
  nameES: string;
  stores: number[];
}

export interface PhysicalStore {
  id: string; /** Store ID */
  name: string; /** Store name, usually based on the street */
  photo: string; /** Store image */
  addressLines: string[]; /** Store address */
  city: string; /** Store city */
  zipCode: string; /** Postal code */
  state?: string; /** Province or state */
  phones: string[]; /** Available phone numbers */
  latitude: number; /** Latitude */
  longitude: number; /** Longitude */
  url: string; /** Store URL */
  schedule?: PhysicalStoreSchedule[];
}

export interface PhysicalStoreSchedule {
  hours: string[];
  days: number[];
  daysText?: string;
}

export interface PhysicalStoresOptions {
  favouriteStores?: boolean; /** If true, returns stores saved by the user as favorites */
  lastStores?: boolean; /** If true, returns the latest stores used by the user for purchases */
  closerStores?: boolean; /** If true, returns stores sorted from the center of the coordinates */
  latitude: number; /** Latitude where stores will be searched */
  longitude: number; /** Longitude where stores will be searched */
  minStores?: number; /** Minimum number of stores to return */
  maxStores?: number; /** Maximum number of stores to return */
  receiveEcommerce?: boolean; /** Indicates whether the store has ecommerce */
  countryCode?: string; /** Country where the search will be performed */
  radioMax?: number; /** Maximum search radius given the coordinates */
}
